import React, { useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { AiOutlineCheckCircle, AiOutlineBank } from "react-icons/ai";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { ref as dbRef, push, set, serverTimestamp } from "firebase/database";
import Header from "../../components/protected/Header";
import Footer from "../../components/footer";
import { auth, database } from "../../config/firebase";
import { UserAuth } from "../../context/AuthContext";
import { useTranslations } from "../../translations";
import { LOCAL_PAYMENT } from "../../config/localPayment";

// Fecha de hoy en formato YYYY-MM-DD (hora local).
const todayLocal = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const LocalPayment = () => {
  const { t } = useTranslations();
  const { user } = UserAuth();
  const [searchParams] = useSearchParams();

  const [plan, setPlan] = useState(searchParams.get("plan") === "annual" ? "annual" : "monthly");
  const [method, setMethod] = useState(LOCAL_PAYMENT.methods[0]?.id || "pago_movil");
  const [reference, setReference] = useState("");
  const [bank, setBank] = useState("");
  const [amount, setAmount] = useState("");
  const [paidAt, setPaidAt] = useState(todayLocal());
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const selectedMethod = LOCAL_PAYMENT.methods.find((m) => m.id === method);

  const handleFile = (e) => {
    const f = e.target.files[0] || null;
    setFile(f);
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev); // libera el anterior
      return f ? URL.createObjectURL(f) : null;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      Swal.fire({ icon: "error", title: t("errorTitle"), text: t("proofRequired") });
      return;
    }
    try {
      setLoading(true);
      const uid = auth.currentUser?.uid;
      // 1) Sube el comprobante a Cloudinary (mismo cloud/preset sin firma que usa la app).
      const cloudForm = new FormData();
      cloudForm.append("file", file);
      cloudForm.append("upload_preset", "miabc_online");
      const cloudRes = await fetch("https://api.cloudinary.com/v1_1/dbohppph0/image/upload", {
        method: "POST",
        body: cloudForm,
      });
      if (!cloudRes.ok) throw new Error(t("proofUploadError"));
      const cloudData = await cloudRes.json();
      const proofUrl = cloudData.secure_url;

      // 2) Crea el registro de pago pendiente de conciliación.
      const paymentRef = push(dbRef(database, "payments"));
      await set(paymentRef, {
        uid,
        email: user?.email || null,
        method,
        plan,
        reference: reference.trim(),
        bank: bank.trim() || null,
        amount: amount ? Number(amount) : null,
        paidAt: paidAt || null,
        proofUrl,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      setSubmitted(true);
    } catch (err) {
      Swal.fire({ icon: "error", title: t("errorTitle"), text: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid className="py-4 mx-auto" style={{ maxWidth: 820 }}>
          <div className="text-center mb-4">
            <AiOutlineBank size={44} style={{ color: "#6c5ce7" }} />
            <h2 className="mt-3 mb-2">{t("localPaymentTitle")}</h2>
            <p className="text-muted">{t("localPaymentSubtitle")}</p>
          </div>

          {submitted ? (
            <Card className="text-center p-4 shadow-sm border-0" style={{ borderRadius: 16 }}>
              <Card.Body>
                <AiOutlineCheckCircle size={56} style={{ color: "#00b894" }} />
                <h4 className="mt-3">{t("paymentUnderReviewTitle")}</h4>
                <p className="text-muted mb-0">{t("paymentUnderReviewText")}</p>
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-4">
              {/* Instrucciones / datos de la cuenta */}
              <Col md={5}>
                <Card className="h-100 shadow-sm border-0" style={{ borderRadius: 16 }}>
                  <Card.Body className="p-4">
                    <h6 className="text-uppercase text-muted mb-3" style={{ letterSpacing: 1 }}>
                      {t("payToAccount")}
                    </h6>
                    <Form.Select
                      className="mb-3"
                      value={method}
                      onChange={(e) => setMethod(e.target.value)}
                    >
                      {LOCAL_PAYMENT.methods.map((m) => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </Form.Select>
                    {selectedMethod?.fields.map((f) => (
                      <div key={f.label} className="mb-2">
                        <div className="small text-muted">{f.label}</div>
                        <div className="fw-semibold">{f.value}</div>
                      </div>
                    ))}
                    <hr />
                    <div className="small text-muted">{t("referencePrice")}</div>
                    <div className="fw-semibold">
                      {LOCAL_PAYMENT.priceHint[plan]} · {plan === "annual" ? t("planAnnual") : t("planMonthly")}
                    </div>
                    {LOCAL_PAYMENT.supportContact ? (
                      <div className="small text-muted mt-3">{LOCAL_PAYMENT.supportContact}</div>
                    ) : null}
                  </Card.Body>
                </Card>
              </Col>

              {/* Formulario del comprobante */}
              <Col md={7}>
                <Card className="h-100 shadow-sm border-0" style={{ borderRadius: 16 }}>
                  <Card.Body className="p-4">
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t("selectPlan")}</Form.Label>
                        <Form.Select value={plan} onChange={(e) => setPlan(e.target.value)}>
                          <option value="monthly">{t("planMonthly")} · {LOCAL_PAYMENT.priceHint.monthly}</option>
                          <option value="annual">{t("planAnnual")} · {LOCAL_PAYMENT.priceHint.annual}</option>
                        </Form.Select>
                      </Form.Group>

                      <Row>
                        <Col xs={12} md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("paymentReference")}</Form.Label>
                            <Form.Control
                              value={reference}
                              onChange={(e) => setReference(e.target.value)}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("originBank")}</Form.Label>
                            <Form.Control value={bank} onChange={(e) => setBank(e.target.value)} />
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("amountPaid")}</Form.Label>
                            <Form.Control
                              type="number"
                              step="0.01"
                              value={amount}
                              onChange={(e) => setAmount(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>{t("paymentDate")}</Form.Label>
                            <Form.Control
                              type="date"
                              value={paidAt}
                              onChange={(e) => setPaidAt(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-4">
                        <Form.Label>{t("uploadProof")}</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleFile}
                          required
                        />
                        <Form.Text className="text-muted">{t("proofHint")}</Form.Text>
                        {preview && (
                          <div className="mt-3 text-center">
                            <img
                              src={preview}
                              alt={t("uploadProof")}
                              className="img-fluid rounded border"
                              style={{ maxHeight: 260, objectFit: "contain" }}
                            />
                          </div>
                        )}
                      </Form.Group>

                      <Button
                        type="submit"
                        variant="primary"
                        className="w-100 py-3 fw-semibold text-nowrap"
                        disabled={loading}
                      >
                        {loading ? <Spinner animation="border" size="sm" /> : t("submitPayment")}
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
        <Footer logged={true} />
      </main>
    </div>
  );
};

export default LocalPayment;
